import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { FiguresCard } from "./FiguresCard";

const SESSION_KEY = "fh_chat_session";
const NUDGE_DELAY_MS = 7000;
const NUDGE_AUTOHIDE_MS = 25000;
const GREETING = {
  role: "assistant",
  content:
    "Hi there! I can answer anything about Financial Habits to Succeed — features, pricing, the MoneyMind Quiz, or the monthly contest. What would you like to know?",
};
const MEMBER_GREETING = {
  role: "assistant",
  content:
    "Welcome back! I can help you get the most out of the system — setting up your Spending Plan, building habit loops, the weekly check-in rhythm, anything. Where would you like a hand?",
};
const STARTERS = [
  "What do I get with membership?",
  "How does the MoneyMind Quiz work?",
  "Tell me about the monthly contest",
];
const MEMBER_STARTERS = [
  "Help me set up my Spending Plan",
  "How do I build my first habit loop?",
  "What's the weekly check-in rhythm?",
];

function readQuizRecord() {
  try {
    const rec = JSON.parse(localStorage.getItem("fh_quiz_taken") || "null");
    if (rec && typeof rec === "object") {
      return { taken: true, persona: rec.persona || null };
    }
  } catch {
    /* corrupt record */
  }
  return { taken: false, persona: null };
}

function Bubble({ m, idx }) {
  const isUser = m.role === "user";
  const cards = Array.isArray(m.cards) ? m.cards : [];
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`chat-message-${m.role}-${idx}`}
    >
      <div
        className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap rounded-lg ${
          isUser
            ? "bg-[#2C302E] text-white rounded-br-sm"
            : "bg-[#F1EEE6] text-[#2C302E] rounded-bl-sm"
        }`}
      >
        {cards.map((card, i) => (
          <FiguresCard key={i} card={card} idx={`${idx}-${i}`} />
        ))}
        {m.content || (cards.length === 0 && <Loader2 className="h-4 w-4 animate-spin text-[#787671]" />)}
      </div>
    </div>
  );
}

export const ChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [nudge, setNudge] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const inApp = location.pathname.startsWith("/app");
  const onAdminChat = location.pathname.startsWith("/app/admin/chat");
  const greeting = inApp ? MEMBER_GREETING : GREETING;
  const starters = inApp ? MEMBER_STARTERS : STARTERS;

  // Member threads carry their figures: when the signed-in member changes
  // (logout, or a different login on a shared browser) drop the thread.
  const { user } = useAuth();
  const userId = user && user.id ? user.id : null;
  const prevUserRef = useRef(userId);
  useEffect(() => {
    if (prevUserRef.current === userId) return;
    if (prevUserRef.current) localStorage.removeItem(SESSION_KEY);
    prevUserRef.current = userId;
    setMessages([]);
    setLoaded(false);
  }, [userId]);

  useEffect(() => {
    if (!open || loaded) return;
    const sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      setMessages([greeting]);
      setLoaded(true);
      return;
    }
    fetch(`${API}/chat/history/${sid}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setMessages(d.messages && d.messages.length ? d.messages : [greeting]);
        setLoaded(true);
      })
      .catch(() => {
        localStorage.removeItem(SESSION_KEY);
        setMessages([greeting]);
        setLoaded(true);
      });
  }, [open, loaded, greeting]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && loaded) inputRef.current?.focus();
  }, [open, loaded]);

  // Nudge pops out beside the button on every page load, then auto-hides.
  useEffect(() => {
    if (open) return undefined;
    setNudge(false);
    const show = setTimeout(() => setNudge(true), NUDGE_DELAY_MS);
    return () => clearTimeout(show);
  }, [open, location.pathname]);

  useEffect(() => {
    if (!nudge) return undefined;
    const hide = setTimeout(() => setNudge(false), NUDGE_AUTOHIDE_MS);
    return () => clearTimeout(hide);
  }, [nudge]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const quiz = readQuizRecord();
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: localStorage.getItem(SESSION_KEY) || null,
          message: text,
          page: location.pathname,
          quiz_taken: quiz.taken,
          quiz_persona: quiz.persona,
        }),
      });
      if (!res.ok || !res.body) {
        let detail = "";
        try {
          const j = await res.json();
          detail = typeof j.detail === "string" ? j.detail : "";
        } catch {
          /* no body */
        }
        throw new Error(detail || "bad response");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop();
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let evt;
          try {
            evt = JSON.parse(line.slice(6));
          } catch {
            continue;
          }
          if (evt.type === "session" && evt.session_id) {
            localStorage.setItem(SESSION_KEY, evt.session_id);
          } else if (evt.type === "delta" && evt.text) {
            setMessages((m) => {
              const c = [...m];
              const last = c[c.length - 1];
              c[c.length - 1] = { ...last, content: last.content + evt.text };
              return c;
            });
          } else if (evt.type === "card" && evt.card) {
            setMessages((m) => {
              const c = [...m];
              const last = c[c.length - 1];
              c[c.length - 1] = { ...last, cards: [...(last.cards || []), evt.card] };
              return c;
            });
          }
        }
      }
    } catch (err) {
      const friendly =
        err && err.message && err.message !== "bad response"
          ? err.message
          : "Hmm, I couldn't connect just now. Give it another try in a moment.";
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: friendly };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  if (onAdminChat) return null;

  return (
    <>
      {!open && nudge && (
        <div
          className={`fixed z-[70] right-20 ${inApp ? "bottom-24 md:bottom-6" : "bottom-6"} flex items-center gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-500`}
          data-testid="chat-nudge"
        >
          <button
            type="button"
            onClick={() => {
              setNudge(false);
              setOpen(true);
            }}
            className="bg-white border border-[#E6E3DB] shadow-lg rounded-lg rounded-br-sm px-4 py-2.5 text-sm text-[#2C302E] hover:border-[#C05C3D] hover:text-[#C05C3D] transition-colors"
            data-testid="chat-nudge-open"
          >
            Questions? Ask me anything
          </button>
          <button
            type="button"
            onClick={() => setNudge(false)}
            aria-label="Dismiss"
            className="h-5 w-5 rounded-full bg-[#2C302E]/70 text-white flex items-center justify-center hover:bg-[#2C302E]"
            data-testid="chat-nudge-dismiss"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      {!open && (
        <button
          type="button"
          onClick={() => {
            setNudge(false);
            setOpen(true);
          }}
          className={`fixed right-5 z-[70] ${inApp ? "bottom-24 md:bottom-5" : "bottom-5"} h-12 w-12 rounded-full bg-[#2C302E] text-white shadow-xl hover:bg-[#C05C3D] transition-colors duration-200 flex items-center justify-center`}
          aria-label="Open chat"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className={`fixed z-[75] flex flex-col bg-white border border-[#E6E3DB] shadow-2xl rounded-lg overflow-hidden left-2 right-2 bottom-2 top-20 sm:left-auto sm:top-auto sm:right-5 ${inApp ? "sm:bottom-5 md:sm:bottom-5" : "sm:bottom-5"} sm:w-96 sm:h-[560px] sm:max-h-[calc(100vh-6rem)]`}
          data-testid="chat-widget-panel"
        >
          <div className="bg-[#2C302E] text-white px-4 py-3 flex items-start justify-between gap-3 shrink-0">
            <div>
              <p className="font-serif text-lg leading-tight">Ask us anything.</p>
              <p className="text-[11px] text-white/60 mt-0.5">
                AI guide · answers in seconds
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white p-1 -mr-1"
              aria-label="Close chat"
              data-testid="chat-close-button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3 bg-[#FBFAF6]"
            data-testid="chat-messages"
          >
            {!loaded ? (
              <div className="flex items-center gap-2 text-sm text-[#787671]">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                {messages.map((m, i) => (
                  <Bubble key={i} m={m} idx={i} />
                ))}
                {messages.length <= 1 && (
                  <div className="pt-1 space-y-2" data-testid="chat-starters">
                    {starters.map((s, i) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        className="block w-full text-left text-sm px-3.5 py-2.5 bg-white border border-[#E6E3DB] rounded-lg text-[#2C302E] hover:border-[#C05C3D] hover:text-[#C05C3D] transition-colors"
                        data-testid={`chat-starter-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-t border-[#E6E3DB] bg-white px-3 py-2.5 shrink-0">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                maxLength={1000}
                className="flex-1 text-sm px-3 py-2 border border-[#E6E3DB] rounded-md focus:outline-none focus:border-[#2C302E] bg-[#FBFAF6]"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="h-9 w-9 shrink-0 rounded-md bg-[#C05C3D] text-white flex items-center justify-center hover:bg-[#A34A2E] disabled:opacity-40 transition-colors"
                aria-label="Send message"
                data-testid="chat-send-button"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
            <p className="mt-1.5 text-[10px] text-[#9a978f] flex items-center justify-between">
              <span>AI assistant · general info, not financial advice</span>
              {readQuizRecord().taken ? (
                <a
                  href="/register?ref=chatbot"
                  className="text-[#C05C3D] hover:underline shrink-0 ml-2"
                  data-testid="chat-register-link"
                >
                  Start your free trial →
                </a>
              ) : (
                <a
                  href="/quiz?ref=chatbot"
                  className="text-[#C05C3D] hover:underline shrink-0 ml-2"
                  data-testid="chat-quiz-link"
                >
                  Take the free quiz →
                </a>
              )}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
